import { parseWikiLinkInner } from '@rbv/shared';
import { parseMarkdownTable, splitTableRow, wikiDisplayText, type ParsedTable } from './helpers.js';
import type { EditorCtx, NavTarget } from './context.js';

/**
 * Light DOM markdown renderer for note-embed widgets (![[note]] transclusion)
 * — non-recursive: nested embeds render as plain links. Not a full CommonMark
 * implementation; covers the constructs a live-preview embed needs.
 */

const RE_CODE = /`([^`\n]+)`/;
const RE_WIKI = /(!?)\[\[([^\[\]\n]+)\]\]/;
const RE_MDLINK = /(!?)\[([^\]\n]*)\]\(([^)\n]+)\)/;
const RE_BOLD = /\*\*([^*\n]+)\*\*|__([^_\n]+)__/;
const RE_ITALIC = /\*([^*\n]+)\*|\b_([^_\n]+)_\b/;
const RE_STRIKE = /~~([^~\n]+)~~/;
const RE_HILITE = /==([^=\n]+)==/;
const RE_TAG = /(^|[\s([{>])#([\p{L}\p{N}_/-]*[\p{L}_/-][\p{L}\p{N}_/-]*)/u;

interface InlinePattern {
  re: RegExp;
  emit(parent: Node, m: RegExpExecArray, ctx: EditorCtx): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

function linkEl(display: string, unresolved: boolean, onFollow: () => void): HTMLElement {
  const a = el('span', unresolved ? 'lp-link lp-link-unresolved' : 'lp-link', display);
  a.setAttribute('role', 'link');
  a.tabIndex = 0;
  a.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
  a.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onFollow();
  });
  return a;
}

function wikiLinkEl(inner: string, ctx: EditorCtx): HTMLElement {
  const w = parseWikiLinkInner(inner, false, 0, 0, `[[${inner}]]`);
  const { id } = ctx.resolve(w.target);
  const t: NavTarget = { target: w.target, heading: w.heading, blockId: w.blockId };
  if (id) t.id = id;
  return linkEl(wikiDisplayText(w), !id, () => ctx.navigate(t));
}

const PATTERNS: InlinePattern[] = [
  {
    re: RE_CODE,
    emit: (p, m) => p.appendChild(el('code', 'lp-code', m[1])),
  },
  {
    re: RE_WIKI,
    // Embeds inside an embed render as plain links (depth 1, non-recursive).
    emit: (p, m, ctx) => p.appendChild(wikiLinkEl(m[2], ctx)),
  },
  {
    re: RE_MDLINK,
    emit: (p, m, ctx) => {
      const href = m[3].split(' ')[0];
      const external = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href) || href.startsWith('//');
      p.appendChild(
        linkEl(m[2] || href, false, () => {
          if (external) window.open(href, '_blank', 'noopener,noreferrer');
          else ctx.navigate({ target: decodeURIComponent(href) });
        }),
      );
    },
  },
  {
    re: RE_BOLD,
    emit: (p, m, ctx) => {
      const e = el('strong');
      renderInlineInto(e, m[1] ?? m[2], ctx);
      p.appendChild(e);
    },
  },
  {
    re: RE_ITALIC,
    emit: (p, m, ctx) => {
      const e = el('em');
      renderInlineInto(e, m[1] ?? m[2], ctx);
      p.appendChild(e);
    },
  },
  {
    re: RE_STRIKE,
    emit: (p, m, ctx) => {
      const e = el('del');
      renderInlineInto(e, m[1], ctx);
      p.appendChild(e);
    },
  },
  {
    re: RE_HILITE,
    emit: (p, m, ctx) => {
      const e = el('mark', 'lp-highlight');
      renderInlineInto(e, m[1], ctx);
      p.appendChild(e);
    },
  },
  {
    re: RE_TAG,
    emit: (p, m) => {
      if (m[1]) p.appendChild(document.createTextNode(m[1]));
      p.appendChild(el('span', 'lp-tag', `#${m[2]}`));
    },
  },
];

/** Render inline markdown into `parent`. */
export function renderInlineInto(parent: Node, text: string, ctx: EditorCtx): void {
  let rest = text;
  while (rest.length > 0) {
    let bestIdx = -1;
    let best: { pat: InlinePattern; m: RegExpExecArray } | null = null;
    for (const pat of PATTERNS) {
      const m = pat.re.exec(rest);
      if (m && (bestIdx === -1 || m.index < bestIdx)) {
        bestIdx = m.index;
        best = { pat, m };
      }
    }
    if (!best) {
      parent.appendChild(document.createTextNode(rest));
      return;
    }
    if (best.m.index > 0) {
      parent.appendChild(document.createTextNode(rest.slice(0, best.m.index)));
    }
    best.pat.emit(parent, best.m, ctx);
    rest = rest.slice(best.m.index + best.m[0].length);
  }
}

/** Build a <table> element from parsed table data. */
export function buildTableDom(t: ParsedTable, ctx: EditorCtx): HTMLElement {
  const wrap = el('div', 'lp-table-wrap');
  const table = el('table', 'lp-table');
  const thead = el('thead');
  const hr = el('tr');
  t.header.forEach((cell, i) => {
    const th = el('th');
    if (t.align[i]) th.style.textAlign = t.align[i]!;
    renderInlineInto(th, cell, ctx);
    hr.appendChild(th);
  });
  thead.appendChild(hr);
  table.appendChild(thead);
  const tbody = el('tbody');
  for (const row of t.rows) {
    const tr = el('tr');
    t.header.forEach((_h, i) => {
      const td = el('td');
      if (t.align[i]) td.style.textAlign = t.align[i]!;
      renderInlineInto(td, row[i] ?? '', ctx);
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  wrap.appendChild(table);
  return wrap;
}

const RE_HEADING = /^(#{1,6})\s+(.*)$/;
const RE_FENCE = /^\s*(```|~~~)\s*(\S*)/;
const RE_HR = /^\s*((-\s*){3,}|(\*\s*){3,}|(_\s*){3,})$/;
const RE_LIST = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const RE_TASK = /^\[([ xX])\]\s+(.*)$/;
const RE_TABLE_LINE = /^\s*\|?.*\|.*$/;
const RE_BLOCKID = /\s\^[A-Za-z0-9-]+\s*$/;

/** Render markdown blocks into `parent` (frontmatter must be pre-stripped). */
export function renderMarkdownInto(parent: HTMLElement, md: string, ctx: EditorCtx): void {
  const lines = md.split('\n');
  let i = 0;
  let list: HTMLElement | null = null;
  let listOrdered = false;

  const closeList = () => {
    list = null;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      closeList();
      i++;
      continue;
    }

    const fence = RE_FENCE.exec(line);
    if (fence) {
      closeList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(fence[1])) {
        code.push(lines[i]);
        i++;
      }
      i++; // closing fence
      const pre = el('pre', 'lp-codeblock');
      pre.appendChild(el('code', undefined, code.join('\n')));
      parent.appendChild(pre);
      continue;
    }

    const h = RE_HEADING.exec(line);
    if (h) {
      closeList();
      const tag = `h${h[1].length}` as keyof HTMLElementTagNameMap;
      const he = el(tag) as HTMLElement;
      renderInlineInto(he, h[2].replace(RE_BLOCKID, ''), ctx);
      parent.appendChild(he);
      i++;
      continue;
    }

    if (RE_HR.test(line)) {
      closeList();
      parent.appendChild(el('hr'));
      i++;
      continue;
    }

    if (trimmed.startsWith('>')) {
      closeList();
      const inner: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        inner.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      const bq = el('blockquote', 'lp-blockquote');
      renderMarkdownInto(bq, inner.join('\n'), ctx);
      parent.appendChild(bq);
      continue;
    }

    // Table: current line + next line must look like header + delimiter.
    if (RE_TABLE_LINE.test(line) && i + 1 < lines.length) {
      const block: string[] = [line];
      let j = i + 1;
      while (j < lines.length && lines[j].trim() !== '' && RE_TABLE_LINE.test(lines[j])) {
        block.push(lines[j]);
        j++;
      }
      const parsed =
        block.length >= 2 && splitTableRow(block[1]).length > 0
          ? parseMarkdownTable(block.join('\n'))
          : null;
      if (parsed) {
        closeList();
        parent.appendChild(buildTableDom(parsed, ctx));
        i = j;
        continue;
      }
    }

    const li = RE_LIST.exec(line);
    if (li) {
      const ordered = /^\d/.test(li[2]);
      if (!list || listOrdered !== ordered) {
        list = el(ordered ? 'ol' : 'ul', 'lp-list');
        listOrdered = ordered;
        parent.appendChild(list);
      }
      const item = el('li');
      const depth = Math.min(Math.floor(li[1].replace(/\t/g, '  ').length / 2), 4);
      if (depth > 0) item.style.marginLeft = `${depth}em`;
      let content = li[3];
      const task = RE_TASK.exec(content);
      if (task) {
        const box = el('input') as HTMLInputElement;
        box.type = 'checkbox';
        box.checked = task[1] !== ' ';
        box.disabled = true;
        box.className = 'lp-task-box';
        item.appendChild(box);
        if (box.checked) item.classList.add('lp-task-done');
        content = task[2];
      }
      renderInlineInto(item, content.replace(RE_BLOCKID, ''), ctx);
      list.appendChild(item);
      i++;
      continue;
    }

    // Paragraph: accumulate until blank line / block start.
    closeList();
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !RE_HEADING.test(lines[i]) &&
      !RE_FENCE.test(lines[i]) &&
      !RE_LIST.test(lines[i]) &&
      !lines[i].trim().startsWith('>')
    ) {
      para.push(lines[i]);
      i++;
    }
    const p = el('p');
    renderInlineInto(p, para.join('\n').replace(RE_BLOCKID, ''), ctx);
    parent.appendChild(p);
  }
}
